import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const FLAGS = { en: "🇬🇧", fr: "🇫🇷", de: "🇩🇪" };
const LABELS = { en: "EN", fr: "FR", de: "DE" };

function LanguageDropdown({ lang, selectLang, isMobile }) {
  const [langOpen, setLangOpen] = useState(false);
  const otherLangs = Object.keys(FLAGS).filter((l) => l !== lang);
  const btnClass = isMobile
    ? "px-2 py-1 border border-[#C8C5BC] text-xs font-bold font-['Cormorant_Garamond'] uppercase tracking-wide text-[#1C2333]/70 inline-flex items-center gap-1.5"
    : "px-3 py-1.5 border border-[#C8C5BC] text-sm font-bold font-['Cormorant_Garamond'] uppercase tracking-wide text-[#1C2333]/70 hover:text-[#C4883A] hover:border-[#C4883A] transition-all duration-200 inline-flex items-center gap-2";
  const itemClass = isMobile
    ? "w-full px-3 py-2 text-xs font-bold font-['Cormorant_Garamond'] uppercase tracking-wide text-[#1C2333]/70 hover:text-[#C4883A] hover:bg-[#C8C5BC]/10 transition-all inline-flex items-center gap-1.5"
    : "w-full px-4 py-2 text-sm font-bold font-['Cormorant_Garamond'] uppercase tracking-wide text-[#1C2333]/70 hover:text-[#C4883A] hover:bg-[#C8C5BC]/10 transition-all inline-flex items-center gap-2";
  const flagSize = isMobile ? "text-sm" : "text-base";

  return (
    <div className="relative">
      <button onClick={() => setLangOpen(!langOpen)} data-testid={isMobile ? "lang-toggle-mobile" : "lang-toggle"} className={btnClass}>
        <span className={`${flagSize} leading-none`}>{FLAGS[lang]}</span> {LABELS[lang]} {!isMobile && <ChevronDown size={14} />}
      </button>
      {langOpen && (
        <div className="absolute right-0 mt-1 bg-white border border-[#C8C5BC]/40 shadow-lg z-50">
          {otherLangs.map((l) => (
            <button key={l} data-testid={`lang-select-${l}`} onClick={() => { selectLang(l); setLangOpen(false); }} className={itemClass}>
              <span className={`${flagSize} leading-none`}>{FLAGS[l]}</span> {LABELS[l]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DesktopNav({ navLinks, pathname }) {
  return (
    <div className="hidden md:flex items-center gap-8">
      {navLinks.map((link) => (
        <Link key={link.path} to={link.path} data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
          className={`text-sm sm:text-base font-bold tracking-wide uppercase transition-colors duration-200 font-['Cormorant_Garamond'] ${pathname === link.path ? "text-[#C4883A]" : "text-[#1C2333]/70 hover:text-[#1B3D2F]"}`}>
          {link.label}
        </Link>
      ))}
    </div>
  );
}

function MobileMenu({ navLinks, pathname, onClose }) {
  return (
    <div data-testid="mobile-menu" className="md:hidden bg-white border-t border-[#C8C5BC]/30 animate-fade-in">
      <div className="px-4 py-4 flex flex-col gap-3">
        {navLinks.map((link) => (
          <Link key={link.path} to={link.path} data-testid={`mobile-nav-link-${link.path.replace("/", "") || "home"}`} onClick={onClose}
            className={`py-2 text-sm sm:text-base font-bold uppercase transition-colors font-['Cormorant_Garamond'] ${pathname === link.path ? "text-[#C4883A]" : "text-[#1C2333]/70"}`}>
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { lang, selectLang, t } = useLanguage();

  const navLinks = [
    { label: t.nav.home, path: "/" },
    { label: t.nav.about, path: "/about" },
    { label: t.nav.book, path: "/book" },
    { label: t.nav.events, path: "/events" },
    { label: t.nav.contact, path: "/contact" },
  ];

  return (
    <nav data-testid="navbar" className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#C8C5BC]/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20 sm:h-36">
        <Link to="/" data-testid="navbar-logo" className="flex items-center">
          <img src="https://customer-assets.emergentagent.com/job_abule-imole/artifacts/6soepudp_Cooperative.png" alt="Abule Imole Village of Light" className="h-14 sm:h-[7.5rem] w-auto" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          <DesktopNav navLinks={navLinks} pathname={location.pathname} />
          <LanguageDropdown lang={lang} selectLang={selectLang} isMobile={false} />
        </div>

        <div className="md:hidden flex items-center gap-3">
          <LanguageDropdown lang={lang} selectLang={selectLang} isMobile={true} />
          <button data-testid="mobile-menu-toggle" className="p-2 text-[#1C2333]" onClick={() => setOpen(!open)}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && <MobileMenu navLinks={navLinks} pathname={location.pathname} onClose={() => setOpen(false)} />}
    </nav>
  );
}
